import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrderTicketNavigation } from '../features/order-ticket/navigation/OrderTicketNavigationContext';
import {
  IntradaySignalBatchUpdate,
  IntradaySignalUpdate,
  IntradayStrategyStartRequest,
  IntradayTradeRow,
} from '../types/trading';
import styles from './IntradaySignalTestPage.module.css';
import { StrategyCard } from './intradaySignalTest/StrategyCard';
import { TradeTable } from './intradaySignalTest/TradeTable';
import {
  buildStrategyRequest,
  createStrategyCard,
  StrategyCardState,
} from './intradaySignalTest/strategyCards';

const MAX_CARDS = 25;
const AUTO_COLLAPSE_AFTER = 3;

const IntradaySignalTestPage: React.FC = () => {
  const {
    wsConnected,
    subscribeToIntradaySignalUpdates,
    startIntradaySignalTest,
    takeOverIntradaySignalTest,
    stopIntradaySignalTest,
  } = useOrderTicketNavigation();

  const [cards, setCards] = useState<StrategyCardState[]>(() => [createStrategyCard()]);
  const [updatesById, setUpdatesById] = useState<Record<string, IntradaySignalUpdate>>({});
  const [trades, setTrades] = useState<IntradayTradeRow[]>([]);
  const [status, setStatus] = useState('idle');
  const [statusError, setStatusError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [chartResetToken, setChartResetToken] = useState(0);

  const builds = useMemo(
    () => cards.map((card, index) => buildStrategyRequest(card, index)),
    [cards],
  );
  const isConfigValid = builds.every((build) => build.valid);
  const isLocked = status === 'locked';

  useEffect(() => {
    return subscribeToIntradaySignalUpdates((batch: IntradaySignalBatchUpdate) => {
      setStatus(batch.status);
      setStatusError(batch.error ?? null);

      if (batch.status === 'started' || batch.status === 'running') setIsRunning(true);
      if (batch.status === 'stopped' || batch.status === 'taken_over' || batch.status === 'locked') {
        setIsRunning(false);
      }

      if (Array.isArray(batch.updates) && batch.updates.length > 0) {
        setUpdatesById((prev) => {
          const next = { ...prev };
          batch.updates?.forEach((update) => {
            next[update.strategy_id] = update;
          });
          return next;
        });
      }

      // Rows ride sampled and lifecycle updates only; keep the last table otherwise.
      if (Array.isArray(batch.trades)) setTrades(batch.trades);
    });
  }, [subscribeToIntradaySignalUpdates]);

  useEffect(() => {
    return () => {
      stopIntradaySignalTest();
    };
  }, [stopIntradaySignalTest]);

  const requests = (): IntradayStrategyStartRequest[] =>
    builds.flatMap((build) => (build.valid ? [build.request] : []));

  const resetRunState = () => {
    setUpdatesById({});
    setTrades([]);
    setChartResetToken((prev) => prev + 1);
  };

  const handleStart = () => {
    if (!isConfigValid) return;
    resetRunState();
    startIntradaySignalTest(requests());
    setIsRunning(true);
  };

  const handleTakeOver = () => {
    if (!isConfigValid) return;
    resetRunState();
    takeOverIntradaySignalTest(requests());
    setIsRunning(true);
  };

  const handleStop = () => {
    stopIntradaySignalTest();
    setIsRunning(false);
  };

  const handleAddCard = useCallback(() => {
    setCards((prev) => {
      if (prev.length >= MAX_CARDS) return prev;
      const next = createStrategyCard();
      const collapsed = prev.length >= AUTO_COLLAPSE_AFTER;
      return [...prev, { ...next, collapsed }];
    });
  }, []);

  const handleCardChange = useCallback((updated: StrategyCardState) => {
    setCards((prev) => prev.map((card) => (card.id === updated.id ? updated : card)));
  }, []);

  const handleRemoveCard = useCallback(
    (card: StrategyCardState) => {
      setCards((prev) => (prev.length === 1 ? prev : prev.filter((item) => item.id !== card.id)));
      setUpdatesById((prev) => {
        const next = { ...prev };
        delete next[card.id];
        return next;
      });
      if (isRunning) stopIntradaySignalTest(card.id);
    },
    [isRunning, stopIntradaySignalTest],
  );

  return (
    <main className={styles.page}>
      <section className={styles.panel}>
        <h1 className={styles.title}>Intraday Signal Test</h1>
        <p className={styles.subTitle}>
          Run several strategies side by side against live ticks. They share one sampling clock, one
          moving average per window, and one trade ledger.
        </p>

        {isLocked ? (
          <div className={styles.lockBanner} data-testid="intraday-lock-banner">
            <span>
              {statusError ??
                'Intraday signal test is already running in another session. Close it first.'}
            </span>
            <button
              type="button"
              onClick={handleTakeOver}
              className={styles.secondaryButton}
              disabled={!isConfigValid}
              data-testid="intraday-take-over-button"
            >
              Take Over
            </button>
          </div>
        ) : null}

        {status === 'error' && statusError ? (
          <div className={styles.lockBanner} data-testid="intraday-error-banner">
            <span>⚠ {statusError}</span>
          </div>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            onClick={handleStart}
            disabled={!isConfigValid}
            className={styles.primaryButton}
            data-testid="intraday-start-button"
          >
            Start
          </button>
          <button
            type="button"
            onClick={handleStop}
            className={styles.secondaryButton}
            disabled={!isRunning}
            data-testid="intraday-stop-button"
          >
            Stop
          </button>
          <button
            type="button"
            onClick={handleAddCard}
            className={styles.secondaryButton}
            disabled={cards.length >= MAX_CARDS}
            data-testid="intraday-add-strategy-button"
          >
            + Add Strategy
          </button>
          <span className={styles.connectionBadge}>
            {wsConnected ? 'WS Connected' : 'WS Disconnected'}
          </span>
          <span className={styles.connectionBadge} data-testid="intraday-run-status">
            Status: {status}
          </span>
        </div>

        <p className={styles.subTitle}>
          Start runs every card together and clears the ledger. Add a card, then press Start again to
          include it.
        </p>

        <div className={styles.strategyList}>
          {cards.map((card, index) => {
            const build = builds[index];
            return (
              <StrategyCard
                key={card.id}
                card={card}
                index={index}
                update={updatesById[card.id] ?? null}
                chartResetToken={chartResetToken}
                removable={cards.length > 1}
                configError={build.valid ? null : build.error}
                onChange={handleCardChange}
                onRemove={() => handleRemoveCard(card)}
              />
            );
          })}
        </div>

        <TradeTable rows={trades} />
      </section>
    </main>
  );
};

export default IntradaySignalTestPage;
